/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

import moment from 'moment';
import { ParsedQuery } from 'query-string';
import React, { PropsWithChildren } from 'react';
import { IntlProvider } from 'react-intl';
import { connect } from 'react-redux';
import { Location } from 'react-router';
import { ToastContainer } from 'react-toastify';
import { Localization } from 'react-widgets';
import MomentLocalizer from 'react-widgets-moment';
import { ITranslateProps, translate } from '../mixins/translate';
import { check } from '../store/authentication/actions';
import { setLanguage } from '../store/language/actions';
import { Roles } from '../utils/enums/Roles';
import { GlobalSettings } from '../utils/getGlobalSettings';
import { appContext, IAppContext } from './appContext';
import { TempLoginComponent as InMaintenance } from './InMaintenance';
import { LanguageDropdownMenu } from './LanguageDropdownMenu';
import { LeftMenu } from './LeftMenu';
import { LoginContainer } from './Login/LoginContainer';
import { TempLogin } from './TempLogin';
import { UserProfileDropdown } from './UserProfileDropdown';
import { CatchingComponent } from './utils/CatchingComponent';

const momentLocalizer = new MomentLocalizer(moment);

type AppProps = PropsWithChildren<
    ITranslateProps & {
        userId: string;
        loggedIn: boolean;
        dispatch: (prop: any) => void;
        currentLanguage: string;
        firstName: string;
        lastName: string;
        companyId: string;
        clientName: string;
        userRole: Roles;
        useBatches: boolean;
        useSurveyComments: boolean;
        isReadOnly: boolean;
        isAccountManager: boolean;
        isConfigurationManager: boolean;
        isDroneOperator: boolean;
        isClient: boolean;
        isTempLogin: boolean;
        isSsoLogin: boolean;
        hasOtpEnabled: boolean;
        globalSettings: GlobalSettings;
        emailAddress: string;
        shouldEnforceMfa?: boolean;
        location: Location & { query: ParsedQuery<string> };
        useEquipmentManagement: boolean;
        useProductTitle: boolean;
        areLabelsVisibleInOverviews: boolean;
    }
>;

interface IAppState {
    meunIsVisible: boolean;
}

function mapStateToProps(state: any) {
    const { authentication, language } = state;
    return {
        loggedIn: authentication.loggedIn,
        currentLanguage: language.code,
        firstName: authentication.firstName,
        lastName: authentication.lastName,
        clientName: authentication.clientName,
        userRole: authentication.role,
        useBatches: authentication.useBatches,
        useSurveyComments: authentication.useSurveyComments,
        isReadOnly: authentication.isReadOnly,
        isAccountManager: authentication.isAccountManager,
        isConfigurationManager: authentication.isConfigurationManager,
        isClient: authentication.isClient,
        isTempLogin: authentication.isTempLogin,
        isSsoLogin: authentication.isSsoLogin,
        isDroneOperator: authentication.isDroneOperator,
        userId: authentication.userId,
        companyId: authentication.companyId,
        hasOtpEnabled: authentication.hasOtpEnabled,
        emailAddress: authentication.emailAddress,
        useEquipmentManagement: authentication.useEquipmentManagement,
        useProductTitle: authentication.useProductTitle,
        areLabelsVisibleInOverviews: authentication.areLabelsVisibleInOverviews,
    };
}

class AppComponent extends React.Component<AppProps, IAppState> {
    constructor(props: AppProps) {
        super(props);

        this.state = {
            meunIsVisible: true,
        };
    }

    public override componentDidMount() {
        if (this.props.location.query.language) {
            this.changeLanguage(this.props.location.query.language as string, true);
        }

        this.props.dispatch(check(false)); // TODO: ZXY-1109
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    }

    public override componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public override UNSAFE_componentWillReceiveProps(nextProps: AppProps) {
        moment.locale(nextProps.currentLanguage);

        if (nextProps.location.pathname !== this.props.location.pathname) {
            window.scrollTo(0, 0);
        }
    }

    public override render() {
        const isSurveyorOrOperatorClient =
            (this.props.isClient && (this.props.userRole === Roles.Operator || this.props.userRole === Roles.Surveyor)) ||
            this.props.userRole === Roles.Administrator;

        const appContextValues: IAppContext = {
            languageCode: this.props.currentLanguage,
            userRole: this.props.userRole,
            isClient: this.props.isClient,
            isDroneOperator: this.props.isDroneOperator,
            companyId: this.props.companyId,
            userId: this.props.userId,
            globalSettings: this.props.globalSettings,
            emailAddress: this.props.emailAddress,
            shouldEnforceMfa: this.props.hasOtpEnabled,
            useProductTitle: this.props.useProductTitle,
            areLabelsVisibleInOverviews: this.props.areLabelsVisibleInOverviews,
        };

        const { isInMaintenance } = this.props.globalSettings;
        if (isInMaintenance) {
            return (
                <appContext.Provider value={appContextValues}>
                    <InMaintenance />
                </appContext.Provider>
            );
        }
        if (this.props.location.query.signature) {
            return (
                <appContext.Provider value={appContextValues}>
                    <TempLogin signature={this.props.location.query.signature as string} surveyId={this.props.location.query.surveyid as string} />
                </appContext.Provider>
            );
        }
        if (!this.props.loggedIn) {
            return (
                <IntlProvider locale={this.props.currentLanguage}>
                    <appContext.Provider
                        value={{
                            languageCode: this.props.currentLanguage,
                            userRole: this.props.userRole,
                            isClient: this.props.isClient,
                            isDroneOperator: this.props.isDroneOperator,
                            companyId: this.props.companyId,
                            userId: this.props.userId,
                            globalSettings: this.props.globalSettings,
                            useProductTitle: this.props.useProductTitle,
                            areLabelsVisibleInOverviews: this.props.areLabelsVisibleInOverviews,
                        }}
                    >
                        <LoginContainer
                            tempLogin={this.props.location.query.templogin === 'true'}
                            passwordToken={this.props.location.query.token as string}
                        />
                    </appContext.Provider>
                </IntlProvider>
            );
        }
        return (
            <IntlProvider locale={this.props.currentLanguage}>
                <appContext.Provider value={appContextValues}>
                    <ToastContainer />
                    <div
                        className={this.state.meunIsVisible ? 'sb-r-c sb-l-o maps-full-page' : 'sb-r-c sb-l-c maps-full-page'}
                        style={{ height: '100%' }}
                    >
                        <div id="main">
                            <header className="navbar navbar-fixed-top navbar-shadow">
                                <div className="navbar-branding">
                                    <a className="navbar-brand" href="#">
                                        <img
                                            src={`/assets/img/${this.props.globalSettings.customerVersion}/logos/logo-small.png`}
                                            title={this.props.clientName}
                                            className="logoImageSmall center-block img-responsive"
                                            style={{ maxWidth: '130px', paddingTop: '7px' }}
                                        />
                                    </a>
                                    <span
                                        className={this.state.meunIsVisible ? 'ad ml40 mt20 hidden-xs hidden-sm' : 'ad ml10 mt20 hidden-xs hidden-sm'}
                                        onClick={() => this.toggleMenu()}
                                    >
                                        {' '}
                                        <i className="fa fs16 fa-align-justify"></i>
                                    </span>
                                </div>

                                <ul className="nav navbar-nav navbar-right">
                                    <LanguageDropdownMenu
                                        changeLanguageClicked={this.changeLanguage.bind(this)}
                                        currentLanguage={this.props.currentLanguage}
                                    />

                                    <UserProfileDropdown
                                        firstName={this.props.firstName}
                                        isSsoLogin={this.props.isSsoLogin}
                                        isTempLogin={this.props.isTempLogin}
                                        lastName={this.props.lastName}
                                    />

                                    {(this.props.clientName === 'AerialsUAS' ||
                                        this.props.clientName === 'PowerFox' ||
                                        this.props.clientName === 'AquaSmartXL' ||
                                        this.props.clientName === 'TechnoSoft') && (
                                        <li>
                                            <img
                                                src={`/assets/img/${this.props.globalSettings.customerVersion}/logos/${this.props.clientName}/logo-small.png`}
                                                title={this.props.clientName}
                                                className="center-block img-responsive"
                                                style={{ maxWidth: '155px' }}
                                            />
                                        </li>
                                    )}
                                </ul>
                            </header>
                            {/* Left menu */}
                            {!this.props.isTempLogin && (
                                <LeftMenu
                                    locationPathname={this.props.location.pathname}
                                    isSurveyorOrOperatorClient={isSurveyorOrOperatorClient}
                                    emailAddress={this.props.emailAddress}
                                    globalSettings={this.props.globalSettings}
                                    useBatches={this.props.useBatches}
                                    useEquipmentManagement={this.props.useEquipmentManagement}
                                    useProductTitle={this.props.useProductTitle}
                                />
                            )}

                            <section id={this.props.isTempLogin ? '' : 'content_wrapper'}>
                                <Localization date={momentLocalizer}>
                                    <CatchingComponent {...this.props}>{this.props.children}</CatchingComponent>
                                </Localization>
                            </section>
                        </div>
                    </div>
                </appContext.Provider>
            </IntlProvider>
        );
    }

    private handleResize(this: void) {
        if (window.innerWidth <= 1000) {
            document.body.className = 'sb-l-m tray-rescale';
        } else {
            document.body.className = '';
        }
    }

    private toggleMenu() {
        this.setState({ meunIsVisible: !this.state.meunIsVisible });
        window.dispatchEvent(new Event('resize'));
    }

    private changeLanguage(langCode: string, shouldSafe = false): void {
        this.props.dispatch(setLanguage(langCode, shouldSafe));
    }
}

// Added `as any` for "Expression produces a union type that is too complex to represent." error.
// eslint-disable-next-line @typescript-eslint/naming-convention
const App = translate()(connect(mapStateToProps)(AppComponent));

// eslint-disable-next-line import/no-default-export
export default App;
